import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_BASE_URL || 'http://localhost:3000';

// Create axios instance
const api = axios.create({
    baseURL: API_BASE_URL,
});

// Request interceptor
api.interceptors.request.use(
    (config) => {
        // Add auth token if available
        const token = localStorage.getItem('token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }

        // Set Content-Type only for non-FormData requests
        if (!(config.data instanceof FormData)) {
            config.headers['Content-Type'] = 'application/json';
        }
        // For FormData, let the browser set the Content-Type with boundary automatically

        return config;
    },
    (error) => Promise.reject(error)
);

// Response interceptor
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            localStorage.removeItem('isAuthenticated');
            window.location.href = '/login';
        }
        return Promise.reject(error);
    }
);

// Categories API
export const categoriesApi = {
    getAll: () => api.get('/api/categories'),
    getById: (id) => api.get(`/api/categories/${id}`),
    create: (data) => api.post('/api/categories', data),
    update: (id, data) => api.put(`/api/categories/${id}`, data),
    delete: (id) => api.delete(`/api/categories/${id}`),
    updateOrder: (order) => api.put('/api/categories/order/update', { category_order: order }),
    toggleVisibility: (id, type, is_hidden) => api.patch('/api/categories/toggle-hidden', { id, type, is_hidden }),
};

// Stories API
export const storiesApi = {
    getAll: (params = {}) => api.get('/api/stories', { params }),
    getById: (id) => api.get(`/api/stories/${id}`),
    create: (data) => api.post('/api/stories', data),
    update: (id, data) => api.put(`/api/stories/${id}`, data),
    patch: (id, data) => api.patch(`/api/stories/${id}`, data),
    delete: (id) => api.delete(`/api/stories/${id}`),
    updateMusicOrder: (categoryId, order) =>
        api.put('/api/stories/music-order/update', { category_id: categoryId, music_order: order }),
    bulkUpload: (data) => api.post('/api/stories/bulk', data),
};

// Tags API
export const tagsApi = {
    getAll: () => api.get('/api/tags'),
    create: (tag) => api.post('/api/tags', { tag }),
    update: (id, tag) => api.put(`/api/tags/${id}`, { tag }),
    delete: (id) => api.delete(`/api/tags/${id}`),
};

// Novel Categories API
export const novelCategoriesApi = {
    getAll: () => api.get('/api/novel-categories'),
    getById: (id) => api.get(`/api/novel-categories/${id}`),
    create: (data) => api.post('/api/novel-categories', data),
    update: (id, data) => api.put(`/api/novel-categories/${id}`, data),
    delete: (id) => api.delete(`/api/novel-categories/${id}`),
    updateOrder: (order) => api.put('/api/novel-categories/order/update', { category_order: order }),
    toggleVisibility: (id, is_hidden) => api.patch('/api/novel-categories/toggle-hidden', { id, is_hidden }),
};

// Novels API
export const novelsApi = {
    getAll: (params = {}) => api.get('/api/novels', { params }),
    getById: (id) => api.get(`/api/novels/${id}`),
    patch: (id, data) => api.patch(`/api/novels/${id}`, data),
    create: (data) => api.post('/api/novels', data),
    update: (id, data) => api.put(`/api/novels/${id}`, data),
    delete: (id) => api.delete(`/api/novels/${id}`),
    updateOrder: (categoryId, order) =>
        api.put('/api/novels/order/update', { category_id: categoryId, novel_order: order }),
    bulkUpload: (data) => api.post('/api/novels/bulk', data),
};

// Dashboard API
export const dashboardApi = {
    getData: () => api.post('/api/data'),
    toggleSetting: () => api.post('/api/setting'),
    toggleUpdate: () => api.post('/api/setting/update'),
};

export default api;
